/**
 * Role gates — the single source of truth for "may this user see this view".
 *
 * These rules were previously hand-derived in both `Sidebar.tsx` (which decides
 * whether a menu entry appears) and `MainContent.tsx` (which decides whether the
 * view behind it renders). The two copies drifted: `canLearning` admitted
 * `training_competency:admin` in the sidebar but not in the router, so a
 * training administrator saw the whole Learning menu and got a blank page on
 * every entry in it.
 *
 * Both files now import from here, so that class of bug cannot recur.
 *
 * **A frontend gate is not a security control.** The backend enforces
 * independently — `middleware/authMiddleware.ts`, `middleware/lmsAuth.ts` and
 * each route's `requireRole`. These gates exist so a URL typed by hand does not
 * render a page the server will refuse, and so a menu entry never leads
 * somewhere the user cannot go. When adding one, check what the backend actually
 * enforces: a gate stricter than the server shows a blank page, looser shows a
 * 403, and the blank page is the one nobody reports.
 */
import { resolveRosterAccess } from './rosterRoles';

/**
 * A populated Role document. The index signature is deliberate: these arrive
 * from Mongoose carrying `_id`, `description` and more, and the screens that
 * receive the user type them that way — only `name` is read here.
 */
export interface RoleDocument {
  [key: string]: unknown;
  name?: string;
}

/** A role as it may arrive from the API — a bare name or a populated document. */
type RoleLike = string | RoleDocument | null | undefined;

/**
 * The parts of the signed-in user these gates read. Deliberately structural:
 * `App.tsx`, `Sidebar.tsx` and `MainContent.tsx` each hold a slightly different
 * user shape, and `extractRoleNames` accepts all of them.
 */
export interface RoleBearingUser {
  role?: string;
  roles?: RoleLike[];
  _computedRoles?: string[];
  // Narrower than `roles` above on purpose: `dbUser.roles` is always populated
  // Role documents, and the screens that receive this user type it that way.
  dbUser?: { roles?: RoleDocument[] } | null;
  email?: string;
}

/**
 * Every role name the user holds, lowercased and de-duplicated, gathered from
 * all four places the API may put them.
 */
export function extractRoleNames(user: RoleBearingUser | null | undefined): string[] {
  const out = new Set<string>();
  const add = (r: RoleLike): void => {
    if (!r) return;
    if (typeof r === 'string') out.add(r.toLowerCase());
    else if (typeof r.name === 'string' && r.name) out.add(r.name.toLowerCase());
  };

  if (Array.isArray(user?._computedRoles)) user._computedRoles.forEach(add);
  if (Array.isArray(user?.roles)) user.roles.forEach(add);
  if (Array.isArray(user?.dbUser?.roles)) user.dbUser.roles.forEach(add);
  if (user?.role) out.add(String(user.role).toLowerCase());

  return Array.from(out);
}

/** True when `roles` contains `slug` (case-insensitive). */
export const hasRole = (roles: string[], slug: string): boolean =>
  roles.includes(slug.toLowerCase());

/** True when any role starts with `prefix` — for role families like `lms:`. */
export const hasRolePrefix = (roles: string[], prefix: string): boolean =>
  roles.some(r => r.startsWith(prefix.toLowerCase()));

/**
 * A platform administrator. The legacy `role: 'admin'` string and the
 * `platform:admin` role grant the same thing; `extractRoleNames` folds the
 * former into the list, so one check covers both.
 */
export const isPlatformAdmin = (roles: string[]): boolean =>
  hasRole(roles, 'admin') || hasRole(roles, 'platform:admin');

/**
 * Roles every internal user may hold, which therefore say nothing about which
 * modules they can reach. Used to identify single-module tenants below.
 */
const BASE_ROLES = new Set([
  'admin',
  'platform:admin',
  'platform:viewer',
  'platform:default',
  'platform:holding',
  'platform:user',
  'holding',
  'user',
  'viewer',
]);

/**
 * A mechanical client tenant: an external user whose *only* module access is
 * `mechanical_clients:*`. They must not reach internal modules by typing a URL.
 * Holding no module role at all is not a tenant — that is an internal user who
 * has simply not been granted anything yet.
 */
export const isMechanicalClientTenant = (roles: string[]): boolean => {
  const moduleRoles = roles.filter(r => !BASE_ROLES.has(r));
  return moduleRoles.length > 0 && moduleRoles.every(r => r.startsWith('mechanical_clients:'));
};

/* --------------------------------- gates --------------------------------- */

/**
 * The Approved Vendor List. Mirrors VENDOR_LIST_ROLES in
 * backend/routes/approvedVendors.ts, which enforces it.
 */
export const canViewVendorList = (roles: string[]): boolean =>
  isPlatformAdmin(roles) ||
  hasRole(roles, 'platform:viewer') ||
  hasRole(roles, 'procurement:admin') ||
  hasRole(roles, 'procurement:vendorlist:viewer') ||
  hasRole(roles, 'procurement_vendors:spend-status');

/**
 * A vendor's money and banking in its detail: purchase history, balances,
 * payments, bank details -- every vendor-list role except a platform viewer.
 * The server withholds these from everyone else (VENDOR_FINANCIAL_ROLES
 * there); this only decides whether to draw the sections.
 */
export const canViewVendorFinancials = (roles: string[]): boolean =>
  isPlatformAdmin(roles) ||
  hasRole(roles, 'procurement:admin') ||
  hasRole(roles, 'procurement:vendorlist:viewer') ||
  hasRole(roles, 'procurement_vendors:spend-status');

/** Vendor prequalification admin: the audit trail, renewal links, prequal screens. */
export const canAdministerVendors = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || hasRole(roles, 'procurement:admin');

export const canJobsAdmin = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || hasRole(roles, 'jobs:admin');

export const canJobFinancials = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || hasRole(roles, 'job_financial:admin');

export const canFinancePl = (roles: string[]): boolean =>
  isPlatformAdmin(roles) ||
  hasRole(roles, 'finance_pl:admin') ||
  hasRole(roles, 'finance_pl:owner') ||
  hasRole(roles, 'finance_pl:board');

/**
 * Cost Transactions has its own role because it exposes supplier, AP invoice
 * and cost/retail margin data that `hr:admin` has no business seeing.
 */
export const canCostTransactionsReport = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || hasRole(roles, 'reports:cost_transactions');

/**
 * A restricted System Administration view — invite/list users and manage
 * mechanical roles only. Full platform admins are unrestricted.
 */
export const isMechanicalAdmin = (roles: string[]): boolean =>
  hasRole(roles, 'mechanical:admin') || hasRole(roles, 'mechanical_psv:admin');

/** The same restricted view, scoped to the per-site training roles. */
export const isTrainingAdmin = (roles: string[]): boolean =>
  hasRole(roles, 'training_competency:admin');

/**
 * May open Minimum Manning for at least one site. Mirrors canView in backend
 * routes/rosterManning.ts, which decides per site and who may edit
 * (`mmr:admin` and platform admins). A roster VIEWER (`rosters:view:<site>`,
 * `rosters:viewer`) needs `mmr:viewer:<site>` as well.
 */
export const canViewMinimumManning = (roles: string[]): boolean => {
  const access = resolveRosterAccess(roles);
  return (
    access.canEdit ||
    isTrainingAdmin(roles) ||
    hasRole(roles, 'training:admin') ||
    hasRole(roles, 'platform:viewer') ||
    hasRole(roles, 'mmr:admin') ||
    hasRolePrefix(roles, 'mmr:viewer:')
  );
};

/**
 * Role Catalog. Mirrors `canOpenRoleCatalog` in backend
 * services/roleDelegation.ts — that file decides which roles each admin may
 * see and grant, and the catalog returns the answer per role, so only "may
 * open it at all" is mirrored here. An `hr:restrict:*` role narrows an HR
 * admin below the full level, which also takes away delegating it.
 */
export const canOpenRoleCatalog = (roles: string[]): boolean =>
  isPlatformAdmin(roles) ||
  isMechanicalAdmin(roles) ||
  isTrainingAdmin(roles) ||
  (hasRole(roles, 'hr:admin') && !hasRolePrefix(roles, 'hr:restrict:'));

/**
 * The operations role: Settings → Notifications (bell and email), and the
 * Scheduled Jobs section of System Administration, and nothing else.
 *
 * Mirrors the backend gate in `routes/notificationAdmin.ts` and
 * `routes/scheduledJobs.ts`, which both accept `notifications:admin`,
 * `platform:admin` or legacy `admin`. Defined here rather than inline so the
 * sidebar and the route cannot drift apart: the last time a role passed one
 * layer and not the other, the screen rendered with nothing on it.
 */
export const isNotificationsAdmin = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || hasRole(roles, 'notifications:admin');

/**
 * Gap Analysis and Compliance. Employee-scoped roles
 * (`training_competency:self`, `training_competency:<siteId>`) must not reach
 * these — they aggregate across the whole workforce.
 */
export const canTrainingAnalytics = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || hasRole(roles, 'hr:admin') || isTrainingAdmin(roles);

/**
 * The certificate approval queue. Mirrors the backend gate exactly
 * (`requireRole(['platform_admin', 'training_competency:admin'])` in
 * `routes/trainingCertificateSubmissions.ts`).
 *
 * Deliberately NOT `canTrainingAnalytics`: that admits `hr:admin`, which the
 * backend refuses, so an HR admin would reach a page that 403s on every call.
 */
export const canTrainingCertificates = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || isTrainingAdmin(roles);

/**
 * The user's own training profile. Reached from the approve/reject notification
 * emails, which link the employee to `?view=MyTrainingProfile`. It only ever
 * shows the signed-in user's own record; the backend scopes every call to them.
 */
export const canOwnTrainingProfile = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || hasRolePrefix(roles, 'training_competency:');

/**
 * Learning (LMS), while the module is gated.
 *
 * Training administrators are admitted by name — they own training content and
 * are the people building it. This mirrors the exception in
 * `backend/middleware/lmsAuth.ts`, which admits `training_competency:admin`
 * while `LMS_VISIBILITY` is unset, and it is the gate whose two hand-written
 * copies disagreed.
 *
 * No `lms:*` role grants visibility yet, and `platform:viewer` is excluded, so
 * `lms:default` can exist and be assignable to future trainees without opening
 * anything today. When the module opens to learners, widen this to
 * `isPlatformAdmin(roles) || hasRolePrefix(roles, 'lms:')` and flip
 * `LMS_VISIBILITY=roles` together — the two must move in step.
 */
export const canLearning = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || isTrainingAdmin(roles);

/** Learning administration — same set as `canLearning` while the module is gated. */
export const canLearningAdmin = canLearning;

/** Employees access from anything other than a team role. */
const canHREmployeesBeyondTeam = (roles: string[]): boolean =>
  isPlatformAdmin(roles) ||
  hasRole(roles, 'hr:admin') ||
  hasRole(roles, 'hr:editor') ||
  hasRole(roles, 'hr:reader') ||
  hasRole(roles, 'hr:training') ||
  hasRole(roles, 'hr:training:editor') ||
  hasRolePrefix(roles, 'hr:site:');

/**
 * HR → Employees. Every global HR role, the training roles, any site-scoped
 * `hr:site:<code>:*` role, and the org-chart team roles (`hr:team:reader`,
 * `hr:team:<name>`); the backend narrows a site-scoped caller to their sites
 * and a team role to the people below that person on the org chart.
 */
export const canHREmployees = (roles: string[]): boolean =>
  canHREmployeesBeyondTeam(roles) || hasRolePrefix(roles, 'hr:team:');

/**
 * HR Insights. Mirrors `canViewInsights` in `getHRPermissions`
 * (`backend/services/hrAccess.ts`), which gates `/insights/summary`: a
 * platform or HR admin unless `hr:restrict:insights` suppresses it, or the
 * dedicated `hr:insights:view`. Restrictions may be hyphen- or comma-joined
 * (`hr:restrict:medical-insights`), as the backend accepts.
 */
export const canHRInsights = (roles: string[]): boolean => {
  const insightsRestricted = roles.some(
    r => r.startsWith('hr:restrict:') &&
      r.slice('hr:restrict:'.length).split(/[-,]/).some(part => part.trim() === 'insights'),
  );
  const isHRAdmin = isPlatformAdmin(roles) || hasRole(roles, 'hr:admin');
  return (isHRAdmin && !insightsRestricted) || hasRole(roles, 'hr:insights:view');
};

/** A per-site training coordinator's role, e.g. `hr:site:39:training`. */
const HR_SITE_TRAINING_ROLE = /^hr:site:[^:]+:training$/;

/**
 * Any HR grant broader than a per-site training role. Mirrors `hasBroaderView`
 * in `backend/services/hrAccess.ts`, which decides `canExport` — the gate on
 * the Sites & Positions endpoints this is used to hide. Keep the two in step.
 */
const isBroaderHRGrant = (role: string): boolean =>
  ['hr:admin', 'hr:editor', 'hr:reader', 'hr:training', 'hr:training:editor'].includes(role) ||
  role.startsWith('hr:medical:') ||
  role.startsWith('hr:employment:') ||
  /^hr:site:[^:]+:(reader|editor|admin)$/.test(role);

/**
 * A training coordinator whose only HR access is one or more
 * `hr:site:<code>:training` roles. They work from Employees and Org Charts
 * alone, so the rest of the HR menu is hidden from them. Roles that grant no
 * HR employee access (`hr:default`, `hr_orgchart:viewer`, `hr:restrict:*`) do
 * not change the answer.
 */
export const isHRSiteTrainingOnly = (roles: string[]): boolean =>
  !isPlatformAdmin(roles) &&
  roles.some(r => HR_SITE_TRAINING_ROLE.test(r)) &&
  !roles.some(isBroaderHRGrant);

/**
 * HR → Performance (probation review tracker). Mirrors `canManageProbation`
 * in `backend/services/probationReviewService.ts`: `hr:performance` or a
 * platform admin. `hr:admin` alone does not grant it.
 */
export const canHRPerformance = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || hasRole(roles, 'hr:performance');

/** HR → Admin. */
export const canHRAdmin = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || hasRole(roles, 'hr:admin') || hasRole(roles, 'hr_employee:admin');

/** HR → Sites & Positions. Not a team role: its endpoints are workforce reports, which a team role never reaches. */
export const canHRSitesPositions = (roles: string[]): boolean =>
  (canHREmployeesBeyondTeam(roles) || canHRAdmin(roles)) && !isHRSiteTrainingOnly(roles);

/**
 * Purchase order administration — the `isAdmin` flag the Purchase Orders screen
 * takes. Note `procurement:admin` is a different role from
 * `procurement_request:admin` above; both exist and they grant different things.
 */
export const canProcurementAdmin = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || hasRole(roles, 'procurement:admin');

/**
 * Procurement → Jobs — the purchasing tracker (quotes, POs, GreenTree
 * commitments) for the jobs you manage. The server lists JM/SM jobs only
 * (`GET /api/job-procurement/jobs`); this gate only reveals the menu entry.
 */
export const canProcurementJobs = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || hasRole(roles, 'procurement_jobs:user');

/**
 * The Homepage Content admin screen — announcements (`comms:author`) and
 * Did You Know tips (`tips:author`).
 *
 * Coarse on purpose: either role opens the screen, and the screen then asks the
 * server (`GET /api/homepage-content/permissions`) which tabs to render. The
 * backend (`routes/homepageContent.ts`) is the authority on each tab; this
 * only decides whether the menu entry and the route exist.
 */
export const canManageHomepageContent = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || hasRole(roles, 'comms:author') || hasRole(roles, 'tips:author');

/**
 * The Approval Desk (`approvals:desk`; backend `opensApprovalsDesk` in
 * config/approverRoles.ts). The server grants the role to anyone with approval
 * authority when they load the app, so a role granted mid-session also arrives
 * as `isApprover` from `GET /api/home/landing` (hooks/useLandingProfile.ts).
 * Approving itself is never gated by this: the existing approval screens and
 * every approve endpoint check the item.
 */
export const canOpenApprovalsDesk = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || hasRole(roles, 'approvals:desk');

/** Approving PSV reports — by role, never by their author. Mirrors CAN_APPROVE in backend routes/psvReports.ts. */
export const canApprovePsvReports = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || hasRole(roles, 'mechanical_psv:admin');

/** The org-wide "All approvals" expense queue. Mirrors the backend's hasFinanceAdmin(). */
export const canSeeAllExpenseApprovals = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || hasRole(roles, 'financial_expenses:admin');

/** The timesheet approvals admin scope (every submitted week, not only one's own). */
export const isTimesheetAdmin = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || hasRole(roles, 'timesheets:admin');

/**
 * Timesheet Reports beyond Fatigue Alerts (Overview, Entries, Utilisation):
 * every employee's hours, leave included. Mirrors `hasTimesheetReport` in
 * routes/timesheetReports.ts; an approver gets Fatigue Alerts only.
 */
export const canViewAllTimesheetReports = (roles: string[]): boolean =>
  isTimesheetAdmin(roles) || hasRole(roles, 'timesheets:report');

/**
 * Critical fatigue alerts: the people who can open Timesheet Reports → Fatigue
 * Alerts, which the alert links to. `timesheets:default` ("submit own") is not
 * one of them.
 */
export const canSeeFatigueAlerts = (roles: string[]): boolean =>
  isTimesheetAdmin(roles) || hasRole(roles, 'timesheets:report') || hasRole(roles, 'timesheets:approve');

/**
 * Giving shoutouts on Home, hiding them, and maintaining the company values
 * list. The backend's gate (`canAuthorShoutouts` in routes/shoutouts.ts) is the
 * authority; this only decides whether the controls are offered.
 */
export const canGiveShoutouts = (roles: string[]): boolean =>
  isPlatformAdmin(roles) || hasRole(roles, 'shoutouts:author');
